// src/components/layout/nav-items.ts
import {
  LayoutDashboard,
  Users,
  UserPlus,
  ClipboardCheck,
  Trophy,
  Scale,
  Flag,
  ArrowLeftRight,
  BookOpen,
  Shield,
  ShieldAlert,
  Coins,
  Medal,
  FileBarChart,
  Settings,
  FolderTree,
  Megaphone,
  Mail,
  KeyRound,
  Snowflake,
  ScrollText,
  MailWarning,
  Package,
  Banknote,
  HandCoins,
  Store,
  ShieldCheck,
  Home,
  Wallet,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { canOpen } from '@/auth/section-access';
import type { CapabilityVerdict } from '@/auth/capabilities';

export interface NavChild {
  to: string;
  label: string;
}

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  enabled: boolean;
  /** Sub-páginas del árbol (colapsables en el sidebar). */
  children?: NavChild[];
  /** true solo para '/' — el match activo debe ser exacto. */
  exact?: boolean;
}

export interface NavGroup {
  /** Clave estable para tests/persistencia, p. ej. 'operacion'. */
  id: string;
  /** null → sin encabezado (bloque de Panel). */
  label: string | null;
  items: NavItem[];
}

/**
 * The panel's menu. WHO sees each entry is not decided here: it is
 * `SECTION_CAPABILITY` in `auth/section-access.ts`, the one map the router reads
 * too (ko-admin#190). An entry with no key there would be offered to everybody,
 * which the tests forbid.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'inicio',
    label: null,
    items: [{ to: '/', label: 'Panel', icon: LayoutDashboard, enabled: true, exact: true }],
  },
  {
    id: 'operacion',
    label: 'Operación',
    items: [
      {
        to: '/users',
        label: 'Usuarios',
        icon: Users,
        enabled: true,
        children: [
          { to: '/users/kyc', label: 'Verificación KYC' },
          { to: '/users/strikes', label: 'Strikes' },
        ],
      },
      { to: '/listings', label: 'Publicaciones', icon: ClipboardCheck, enabled: true },
      // ko-backend#1090 — applications for the verified-store badge. Next to
      // the listings entry: same reviewers, same marketplace-trust judgement.
      { to: '/stores', label: 'Tiendas verificadas', icon: Store, enabled: true },
      // ko-admin#145 — orders, in all eight statuses. A section of its own
      // rather than another listings tab: the shipment-compliance tab is a
      // QUEUE of orders that broke a deadline, and the ordinary question ("my
      // order never arrived", no dispute) did not fit in it.
      { to: '/orders', label: 'Pedidos', icon: Package, enabled: true },
      // #117 — manual-review queue for trades. Top level and not a child of
      // Disputas: the queue is precisely the trades that have NO dispute.
      { to: '/trades', label: 'Intercambios', icon: ArrowLeftRight, enabled: true },
      { to: '/disputes', label: 'Disputas', icon: Scale, enabled: true },
      // KO Hub — assisted sales held and inspected at the office. Next to
      // orders and disputes: every paid case is an order underneath.
      { to: '/kohub', label: 'KO Hub', icon: ShieldCheck, enabled: true },
      // ko-backend#733 — top level for the same reason as Intercambios: these
      // are exactly the orders with NO dispute, and the only surface that
      // reached unfreeze was a dispute detail.
      { to: '/escrow', label: 'Dinero parado', icon: Snowflake, enabled: true },
      // ko-backend#777 — money outcomes the platform left waiting on a person.
      // Next to Escrow congelado because it is the same question (money that
      // will not move on its own).
      { to: '/pending-decisions', label: 'Decisiones pendientes', icon: HandCoins, enabled: true },
      // #83 — user-report moderation queue. Top level rather than a child of
      // "Publicaciones" because a report targets a listing OR a user, and it
      // is deliberately not labelled "Reportes": that name already belongs to
      // the analytics section of the panel.
      { to: '/moderation', label: 'Moderación', icon: Flag, enabled: true },
    ],
  },
  {
    id: 'arena',
    label: 'Arena',
    items: [
      {
        to: '/tournaments',
        label: 'Torneos',
        icon: Trophy,
        enabled: true,
        children: [{ to: '/tournaments/prizes', label: 'Premios' }],
      },
      // #60 — anti-cheat review. Under Arena because what gets reviewed is a
      // tournament match, not a user and not a listing.
      { to: '/anticheat', label: 'Anti-trampa', icon: ShieldAlert, enabled: true },
      {
        to: '/questions',
        label: 'Preguntas',
        icon: BookOpen,
        enabled: true,
        children: [
          { to: '/question-banks', label: 'Bancos y categorías' },
          { to: '/questions/proposals', label: 'Propuestas' },
          // #85 — the route existed and no menu reached it. Under Preguntas
          // because what a creator does is propose questions.
          { to: '/creators', label: 'Creadores' },
        ],
      },
    ],
  },
  {
    id: 'economia',
    label: 'Economía',
    items: [
      { to: '/koins', label: 'KOins', icon: Coins, enabled: true },
      // ko-admin#152 — the money leaving the platform. It hangs off the
      // economy group and not off the operations one: it is not a queue
      // anybody works through, it is the record of transfers that already
      // went out.
      { to: '/payouts', label: 'Payouts', icon: Banknote, enabled: true },
      { to: '/tiers', label: 'Tiers', icon: Medal, enabled: true },
      { to: '/campaigns', label: 'Campañas', icon: Megaphone, enabled: true },
      { to: '/referrals', label: 'Referidos', icon: UserPlus, enabled: true },
      { to: '/reports', label: 'Reportes', icon: FileBarChart, enabled: true },
    ],
  },
  {
    id: 'administracion',
    label: 'Administración',
    items: [
      { to: '/staff', label: 'Personal', icon: Shield, enabled: true },
      { to: '/invitations', label: 'Invitaciones', icon: Mail, enabled: true },
      { to: '/taxonomy', label: 'Taxonomía', icon: FolderTree, enabled: true },
      { to: '/config', label: 'Configuración', icon: Settings, enabled: true },
      // #195 — the platform's audit log. Out of Personal, where it lived as a
      // tab: what it records is not what the staff did but every sensitive
      // decision, and under that heading the PRD audit read the panel and
      // concluded there was none.
      { to: '/audit', label: 'Auditoría', icon: ScrollText, enabled: true },
      // ko-backend#800 — outbox rows that ran out of attempts. Under the `administracion`
      // group and not `operacion`: it is platform plumbing (events that reached nobody),
      // acted on by whoever got the relay's alert.
      { to: '/outbox', label: 'Eventos sin entregar', icon: MailWarning, enabled: true },
      // #86 — MFA de la propia cuenta (re-enrolamiento autenticado).
      { to: '/security', label: 'Seguridad', icon: KeyRound, enabled: true },
    ],
  },
];

/**
 * 2026-11-02 — the store owner's menu, apart from the platform's groups: a
 * role USER with an active store sees this and nothing of `NAV_GROUPS`, and
 * the platform's operators never see it (`SECTION_CAPABILITY` says `STORE`).
 * An ADMIN reads stores from the verified-stores screen of the operations group.
 */
export const STORE_NAV_GROUP: NavGroup = {
  id: 'tienda',
  label: 'Mi tienda',
  items: [
    { to: '/tienda', label: 'Inicio de la tienda', icon: Home, enabled: true, exact: true },
    { to: '/tienda/publicaciones', label: 'Publicaciones', icon: ClipboardCheck, enabled: true },
    { to: '/tienda/pedidos', label: 'Ventas', icon: Package, enabled: true },
    { to: '/tienda/kohub', label: 'KO Hub', icon: ShieldCheck, enabled: true },
    { to: '/tienda/cobros', label: 'Cobros', icon: Wallet, enabled: true },
    { to: '/tienda/perfil', label: 'Perfil de la tienda', icon: UserRound, enabled: true },
  ],
};

/**
 * The nav as `role` may actually use it: every entry, and every child, that
 * `canOpen` lets through.
 *
 * Groups left with no items are dropped rather than rendered as a bare heading:
 * a section title with nothing under it reads as a section that failed to load.
 *
 * `verdict` answers for one capability; omitting it treats every capability as
 * unknown, which `canOpen` lets through.
 */
export function navGroupsFor(
  role: string | null,
  verdict: (capability: string) => CapabilityVerdict = () => 'unknown',
): NavGroup[] {
  const open = (to: string) => canOpen(to, role, verdict);
  const groups = role === 'USER' ? [STORE_NAV_GROUP] : NAV_GROUPS;
  return groups
    .map((g) => ({
      ...g,
      items: g.items
        .filter((i) => open(i.to))
        .map((i) => (i.children ? { ...i, children: i.children.filter((c) => open(c.to)) } : i)),
    }))
    .filter((g) => g.items.length > 0);
}

/** Compat: lista plana de items de nivel 1 (breadcrumbs y tests existentes). */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/** Busca el label humano de una ruta en items y children (para breadcrumbs). */
export function findNavLabel(path: string): string | undefined {
  for (const group of [...NAV_GROUPS, STORE_NAV_GROUP]) {
    for (const item of group.items) {
      if (item.to === path) return item.label;
      const child = item.children?.find((c) => c.to === path);
      if (child) return child.label;
    }
  }
  return undefined;
}
